// --- UPDATE (FlowSense AI Explainer + real-data pass)
//   • A non-array response is read with asList(); colours are assigned after sorting so each
//     type keeps its colour position in the ranking.
//   • Live: refetches on work order changes (work_order_events), debounced.
//   • "Explain by asset type" FlowSense AI explainer: concentration of work by asset type.
// --- END UPDATE
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Heading,
  Flex,
  Text,
  useColorModeValue,
  HStack,
  VStack,
  Badge,
  IconButton,
  Tooltip,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Switch,
  FormControl,
  FormLabel,
  Collapse,
  Code,
  Divider,
  Spinner,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Select,
  Button,
  Tag,
  TagLabel,
} from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import {
  MdRefresh,
  MdDownload,
  MdSettings,
  MdBugReport,
  MdFullscreen,
  MdFullscreenExit,
} from 'react-icons/md';
import { FaChartBar, FaChartLine, FaChartArea } from 'react-icons/fa';
import { BsLayoutSidebarInset } from 'react-icons/bs';
import Card from '../card/Cards';

// --- CRITICAL UPDATE: Swap to Analytics Service to prevent 10k data dump ---
import analyticsService, { asList } from '../../services/analyticsService';
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer'; // --- UPDATE
import useLiveRefresh from '../../hooks/useLiveRefresh';       // --- UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// Asset-type color palette — cycles through 12 distinct enterprise colors
// ─────────────────────────────────────────────────────────────────────────────
const ASSET_PALETTE = [
  '#4299E1', '#38A169', '#D69E2E', '#E53E3E',
  '#805AD5', '#2B6CB0', '#276749', '#B7791F',
  '#C53030', '#553C9A', '#00B5D8', '#DD6B20',
];

const getAssetColor = (index) => ASSET_PALETTE[index % ASSET_PALETTE.length];

// ─────────────────────────────────────────────────────────────────────────────
// Custom Recharts Tooltip
// ─────────────────────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, cardBg, borderColor }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="md"
      px={3}
      py={2}
      boxShadow="lg"
      fontSize="sm"
      minW="160px"
    >
      <Text fontWeight="bold" mb={1} noOfLines={2}>{label}</Text>
      {payload.map((entry, i) => (
        <Flex key={i} align="center" gap={2}>
          <Box w={2} h={2} borderRadius="full" bg={entry.color || entry.fill} />
          <Text color="gray.500">{entry.name}:</Text>
          <Text fontWeight="semibold">{entry.value}</Text>
        </Flex>
      ))}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const WorkOrderAssetTypeChart = ({
  data: externalData,
  isLoading: externalLoading,
  enableDebug  = false,
  enableExport = true,
  historicalData = null,
  onAssetTypeClick,
}) => {
  // ── Theme ──────────────────────────────────────────────────────────────────
  const chartTextColor = useColorModeValue('#1A202C', '#FFFFFF');
  const cardBg         = useColorModeValue('white', 'gray.800');
  const borderColor    = useColorModeValue('gray.200', 'gray.600');
  const debugBg        = useColorModeValue('gray.50', 'gray.700');
  const gridColor      = useColorModeValue('#E2E8F0', '#4A5568');
  const mutedColor     = useColorModeValue('gray.500', 'gray.400');

  // ── State ──────────────────────────────────────────────────────────────────
  const [chartType,          setChartType]          = useState('horizontal-bar'); // default: original layout
  const [showDebug,          setShowDebug]          = useState(enableDebug);
  const [debugLogs,          setDebugLogs]          = useState([]);
  const [isFullscreen,       setIsFullscreen]       = useState(false);
  const [selectedAssetType,  setSelectedAssetType]  = useState(null);
  const [showDataLabels,     setShowDataLabels]     = useState(true);
  const [showGrid,           setShowGrid]           = useState(true);
  const [showLegend,         setShowLegend]         = useState(true);
  const [useAssetColors,     setUseAssetColors]     = useState(true);
  const [performanceMetrics, setPerformanceMetrics] = useState({
    renderTime: 0,
    dataPoints: 0,
    lastUpdate: null,
  });

  // ── 🚀 ADVANCED DEBUGGING: Render Tracking ────────────────────────────────
  const renderCount = useRef(0);
  renderCount.current += 1;

  // ── Internal data fetching ─────────────────────────────────────────────────
  const [internalData,    setInternalData]    = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);

  // Determine active data source (Props override internal state if provided)
  const activeData    = externalData || internalData;
  const activeLoading = externalLoading !== undefined ? externalLoading : internalLoading;

  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();
  const toast = useToast();

  // ── 🚀 ADVANCED DEBUGGING: Enhanced Debug Logger ──────────────────────────
  const addDebugLog = useCallback((message, type = 'info', details = null) => {
    if (!enableDebug && !showDebug) return;

    const timestamp = new Date().toISOString();
    let safeDetails = details;
    try {
      if (details instanceof Error) {
        safeDetails = { message: details.message, name: details.name, stack: details.stack };
      } else if (typeof details === 'object' && details !== null) {
        safeDetails = JSON.parse(JSON.stringify(details, (key, value) =>
          typeof value === 'function' ? '[Function]' : value
        ));
      }
    } catch (e) {
      safeDetails = '[Unserializable Object - See Browser Console]';
    }

    const log = { timestamp, type, message, details: safeDetails };
    setDebugLogs(prev => [log, ...prev].slice(0, 100));

    if (type === 'error') {
      console.error(`[AssetTypeChart] ❌ ${message}`, details);
      console.trace();
    } else if (type === 'warn') {
      console.warn(`[AssetTypeChart] ⚠️ ${message}`, details);
    } else {
      console.log(`[AssetTypeChart] ℹ️ ${message}`, details);
    }
  }, [enableDebug, showDebug]);

  // ── 🚀 ADVANCED DEBUGGING: Lifecycle Monitors ─────────────────────────────
  useEffect(() => {
    if (renderCount.current % 5 === 0) {
      addDebugLog(`Component Rendered Cycle (Count: ${renderCount.current})`, 'info', {
        chartType,
        activeLoading,
      });
    }
  });

  useEffect(() => {
    addDebugLog('Input Data Source Changed', 'info', {
      hasExternalData:  !!externalData,
      internalDataSize: internalData.length,
      activeDataIsArray: Array.isArray(activeData),
      activeDataSize:   activeData ? activeData.length : 0,
      sampleHead:       Array.isArray(activeData) ? activeData.slice(0, 2) : 'Not an Array',
    });
  }, [activeData, externalData, internalData, addDebugLog]);

  // ── 🚀 CORE LOGIC: Fetch — DO NOT MODIFY ──────────────────────────────────
  const fetchAssetData = useCallback(async (forceRefresh = false) => {
    if (externalData) {
      addDebugLog('fetchAssetData skipped: externalData prop is managing state', 'warn');
      return;
    }

    setInternalLoading(true);
    const startTime = performance.now();
    addDebugLog(`Initiating data fetch (forceRefresh: ${forceRefresh})`, 'info');

    try {
      const response = await analyticsService.getWidgetData('work-orders-by-asset-type');
      const parsedData = asList(response); // --- UPDATE: always an array

      addDebugLog('Raw payload received from API', 'info', {
        payloadType: typeof parsedData,
        isArray:     Array.isArray(parsedData),
      });

      setInternalData(parsedData);

      const fetchTime = performance.now() - startTime;
      addDebugLog('Asset type data fetched successfully', 'success', {
        ms: fetchTime.toFixed(2),
      });

      if (forceRefresh) {
        toast({
          title:       'Data Refreshed',
          description: 'Synced asset type analytics from database.',
          status:      'success',
          duration:    2000,
          isClosable:  true,
        });
      }
    } catch (error) {
      addDebugLog(`Fetch failed: ${error.message}`, 'error', error);
      toast({
        title:       'Error fetching asset type data',
        description: error.message,
        status:      'error',
        duration:    5000,
        isClosable:  true,
      });
    } finally {
      setInternalLoading(false);
    }
  }, [externalData, addDebugLog, toast]);

  // Initial load
  useEffect(() => { fetchAssetData(); }, [fetchAssetData]);
  useLiveRefresh(['work_order_events'], () => fetchAssetData(false), { enabled: !externalData, accept: (b) => !b?.type || ['created', 'updated', 'status_updated', 'assigned', 'completed', 'closed', 'deleted', 'priority_changed'].includes(b.type) }); // --- UPDATE

  // ── Normalized Data ────────────────────────────────────────────────────────
  // Validates shape; enriches each row with color and percentage
  const normalizedData = useMemo(() => {
    const startTime = performance.now();

    if (!activeData || !Array.isArray(activeData) || activeData.length === 0) {
      addDebugLog('NormalizedData aborted: activeData is empty or not an array', 'warn');
      return null;
    }

    const total = activeData.reduce((sum, item) => sum + (Number(item.work_order_count) || 0), 0);

    const result = activeData.map((item, index) => ({
      // --- ORIGINAL FIELD KEYS PRESERVED ---
      type:              item.type || item.asset_type || item.name || `Type ${index + 1}`,
      work_order_count:  Number(item.work_order_count) || 0,
      // --- ENRICHMENT FIELDS ---
      percentage:        total > 0 ? ((Number(item.work_order_count) || 0) / total * 100).toFixed(1) : '0.0',
      color:             getAssetColor(index),
    }));

    // Sort descending by count for cleaner visual ranking
    result.sort((a, b) => b.work_order_count - a.work_order_count);
    result.forEach((r, i) => { r.color = getAssetColor(i); }); // --- UPDATE: colour by rank

    const renderTime = performance.now() - startTime;
    setPerformanceMetrics({
      renderTime: renderTime.toFixed(2),
      dataPoints: result.length,
      lastUpdate: new Date(),
    });

    addDebugLog('NormalizedData computed', 'success', {
      rows:  result.length,
      total,
      ms:    renderTime.toFixed(2),
    });

    return result;
  }, [activeData, addDebugLog]);

  // Trace normalizer
  useEffect(() => {
    addDebugLog('Normalized Data Evaluated', 'success', { output: normalizedData });
  }, [normalizedData, addDebugLog]);

  // ── Derived Summary ────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    if (!normalizedData || normalizedData.length === 0) return null;

    const total   = normalizedData.reduce((s, i) => s + i.work_order_count, 0);
    const topItem = normalizedData[0]; // already sorted desc
    const lowItem = normalizedData[normalizedData.length - 1];

    let trend = null;
    if (historicalData && Array.isArray(historicalData)) {
      const historicalTotal = historicalData.reduce((s, i) => s + (Number(i.work_order_count) || 0), 0);
      if (historicalTotal > 0) {
        const change = ((total - historicalTotal) / historicalTotal) * 100;
        trend = {
          direction:  change > 0 ? 'increase' : change < 0 ? 'decrease' : 'unchanged',
          percentage: Math.abs(change).toFixed(1),
        };
      }
    }

    return { total, topItem, lowItem, assetCount: normalizedData.length, trend };
  }, [normalizedData, historicalData]);

  // --- UPDATE: FlowSense rule findings
  const typeExplainer = useMemo(() => {
    const findings = [];
    if (!normalizedData || !summary || !summary.total) return { findings, facts: {} };
    const top = normalizedData[0];
    const topShare = (top.work_order_count / summary.total) * 100;
    const top3Share = (normalizedData.slice(0, 3).reduce((s, r) => s + r.work_order_count, 0) / summary.total) * 100;
    findings.push({ severity: topShare >= 40 ? 'medium' : 'info', text: `${top.type} generates ${top.work_order_count} work orders — ${topShare.toFixed(0)}% of the total.` });
    if (normalizedData.length > 3) findings.push({ severity: 'info', text: `The top 3 asset types account for ${top3Share.toFixed(0)}% of work orders.` });
    if (summary.trend) findings.push({ severity: summary.trend.direction === 'increase' ? 'medium' : 'ok', text: `Volume is ${summary.trend.direction === 'increase' ? 'up' : 'down'} ${summary.trend.percentage}% against the comparison period.` });
    return { findings, facts: { total: summary.total, asset_types: normalizedData.map(r => ({ type: r.type, work_orders: r.work_order_count, share_pct: Number(r.percentage) })), trend: summary.trend } };
  }, [normalizedData, summary]);
  // --- END UPDATE

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    if (!normalizedData) return;
    try {
      const exportData = {
        timestamp: new Date().toISOString(),
        chartType,
        data:      normalizedData,
        summary: {
          total:      summary?.total,
          topAsset:   summary?.topItem?.type,
          assetCount: summary?.assetCount,
        },
      };

      const dataStr  = JSON.stringify(exportData, null, 2);
      const dataUri  = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
      const link     = document.createElement('a');
      link.setAttribute('href', dataUri);
      link.setAttribute('download', `work-orders-by-asset-type-${Date.now()}.json`);
      link.click();

      addDebugLog('Data exported successfully', 'success');
      toast({
        title: 'Export Complete',
        description: 'Asset type data has been exported.',
        status: 'success', duration: 2000, isClosable: true,
      });
    } catch (error) {
      addDebugLog(`Export failed: ${error.message}`, 'error', error);
      toast({
        title: 'Export Failed',
        description: error.message,
        status: 'error', duration: 3000, isClosable: true,
      });
    }
  }, [normalizedData, summary, chartType, addDebugLog, toast]);

  // ── Stats Row ──────────────────────────────────────────────────────────────
  const renderStats = () => {
    if (!normalizedData || !summary) return null;

    const statItems = [
      {
        label:  'Total Work Orders',
        value:  summary.total,
        help:   `Across ${summary.assetCount} asset types`,
        scheme: 'blue',
        trend:  summary.trend,
      },
      {
        label:  'Asset Types',
        value:  summary.assetCount,
        help:   'Distinct categories',
        scheme: 'purple',
      },
      {
        label:  'Top Asset Type',
        value:  summary.topItem?.work_order_count ?? 0,
        help:   summary.topItem?.type ?? '—',
        scheme: 'green',
      },
      {
        label:  'Lowest Activity',
        value:  summary.lowItem?.work_order_count ?? 0,
        help:   summary.lowItem?.type ?? '—',
        scheme: 'orange',
      },
    ];

    return (
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} mb={4}>
        {statItems.map((item) => (
          <Stat
            key={item.label}
            size="sm"
            p={2}
            borderRadius="md"
            borderWidth="1px"
            borderColor={borderColor}
            bg={cardBg}
            transition="all 0.2s"
            _hover={{ borderColor: `${item.scheme}.400`, transform: 'translateY(-2px)' }}
          >
            <StatLabel fontSize="xs" color="gray.500">{item.label}</StatLabel>
            <StatNumber fontSize="lg">
              <Badge colorScheme={item.scheme} variant="subtle" fontSize="md" px={1}>
                {item.value}
              </Badge>
            </StatNumber>
            <StatHelpText fontSize="xs" mb={0}>
              {item.trend ? (
                <>
                  <StatArrow type={item.trend.direction === 'increase' ? 'increase' : 'decrease'} />
                  {item.trend.percentage}% vs prev
                </>
              ) : (
                item.help
              )}
            </StatHelpText>
          </Stat>
        ))}
      </SimpleGrid>
    );
  };

  // ── 🚀 CORE LOGIC: Chart Renderer — ORIGINAL DATA KEYS PRESERVED ──────────
  const renderChart = () => {
    const height = isFullscreen ? 550 : 300;

    if (!normalizedData || normalizedData.length === 0) {
      return (
        <Flex justify="center" align="center" h={`${height}px`} direction="column">
          <Text color={mutedColor} fontSize="lg" mb={2}>No asset type data available.</Text>
          <Text color="gray.400" fontSize="sm">Check the analytics service connection.</Text>
        </Flex>
      );
    }

    const yAxisWidth = Math.min(
      180,
      Math.max(80, Math.max(...normalizedData.map(d => d.type.length)) * 7)
    );

    const commonProps = {
      data:   normalizedData,
      margin: { top: 10, right: 30, left: 0, bottom: 4 },
    };

    const tooltip = (
      <RechartsTooltip
        content={<CustomTooltip cardBg={cardBg} borderColor={borderColor} />}
        cursor={{ fill: 'rgba(130,130,200,0.08)' }}
      />
    );

    const legend = showLegend && (
      <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
    );

    // ── Horizontal Bar (default — ORIGINAL layout) ─────────────────────────
    if (chartType === 'horizontal-bar') {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart {...commonProps} layout="vertical">
            {showGrid && <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridColor} />}
            <XAxis
              type="number"
              stroke={chartTextColor}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: gridColor }}
            />
            <YAxis
              dataKey="type"
              type="category"
              width={yAxisWidth}
              stroke={chartTextColor}
              tick={{ fontSize: 11, fontWeight: 600 }}
              tickLine={false}
              axisLine={false}
            />
            {tooltip}
            {legend}
            <Bar
              dataKey="work_order_count"
              name="Work Orders"
              radius={[0, 4, 4, 0]}
              isAnimationActive
            >
              {showDataLabels && (
                <LabelList
                  dataKey="work_order_count"
                  position="right"
                  style={{ fontSize: 11, fontWeight: 600, fill: chartTextColor }}
                />
              )}
              {normalizedData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={useAssetColors ? entry.color : '#4299E1'}
                  cursor="pointer"
                  onClick={() => {
                    setSelectedAssetType(entry.type);
                    if (onAssetTypeClick) onAssetTypeClick(entry.type);
                  }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }

    // ── Vertical Bar ───────────────────────────────────────────────────────
    if (chartType === 'vertical-bar') {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} />}
            <XAxis
              dataKey="type"
              stroke={chartTextColor}
              tick={{ fontSize: 10, fontWeight: 600 }}
              tickLine={false}
              axisLine={{ stroke: gridColor }}
              interval={0}
              angle={-25}
              textAnchor="end"
              height={60}
            />
            <YAxis
              stroke={chartTextColor}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={36}
            />
            {tooltip}
            {legend}
            <Bar
              dataKey="work_order_count"
              name="Work Orders"
              radius={[4, 4, 0, 0]}
              isAnimationActive
            >
              {showDataLabels && (
                <LabelList
                  dataKey="work_order_count"
                  position="top"
                  style={{ fontSize: 11, fontWeight: 600, fill: chartTextColor }}
                />
              )}
              {normalizedData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={useAssetColors ? entry.color : '#4299E1'}
                  cursor="pointer"
                  onClick={() => {
                    setSelectedAssetType(entry.type);
                    if (onAssetTypeClick) onAssetTypeClick(entry.type);
                  }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }

    // ── Line Chart ─────────────────────────────────────────────────────────
    if (chartType === 'line') {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} />}
            <XAxis
              dataKey="type"
              stroke={chartTextColor}
              tick={{ fontSize: 10, fontWeight: 600 }}
              tickLine={false}
              axisLine={{ stroke: gridColor }}
              interval={0}
              angle={-25}
              textAnchor="end"
              height={60}
            />
            <YAxis
              stroke={chartTextColor}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={36}
            />
            {tooltip}
            {legend}
            <Line
              type="monotone"
              dataKey="work_order_count"
              stroke="#4299E1"
              strokeWidth={3}
              dot={{ r: 5, fill: '#4299E1', strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 7 }}
              name="Work Orders"
            />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    // ── Area Chart ─────────────────────────────────────────────────────────
    if (chartType === 'area') {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="assetGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#4299E1" stopOpacity={0.6} />
                <stop offset="95%" stopColor="#4299E1" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            {showGrid && <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} />}
            <XAxis
              dataKey="type"
              stroke={chartTextColor}
              tick={{ fontSize: 10, fontWeight: 600 }}
              tickLine={false}
              axisLine={{ stroke: gridColor }}
              interval={0}
              angle={-25}
              textAnchor="end"
              height={60}
            />
            <YAxis
              stroke={chartTextColor}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={36}
            />
            {tooltip}
            {legend}
            <Area
              type="monotone"
              dataKey="work_order_count"
              stroke="#4299E1"
              strokeWidth={3}
              fill="url(#assetGradient)"
              dot={{ r: 5, fill: '#4299E1', strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 7 }}
              name="Work Orders"
            />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    return null;
  };

  // ── Debug Console ──────────────────────────────────────────────────────────
  const renderDebugConsole = () => (
    <Collapse in={showDebug} animateOpacity>
      <Box
        mt={4} p={3}
        bg={debugBg}
        borderRadius="md"
        borderWidth="1px"
        borderColor={borderColor}
        maxH="300px"
        overflowY="auto"
      >
        <HStack justify="space-between" mb={2}>
          <Text fontWeight="bold" fontSize="sm">Advanced Debug Console</Text>
          <HStack>
            <Badge colorScheme="blue">Renders: {renderCount.current}</Badge>
            <Button size="xs" onClick={() => setDebugLogs([])}>Clear</Button>
          </HStack>
        </HStack>

        {/* Performance Metrics */}
        <Box mb={2} p={2} bg={cardBg} borderRadius="md">
          <Text fontSize="xs" fontWeight="bold" mb={1}>Performance Metrics:</Text>
          <SimpleGrid columns={3} spacing={2} fontSize="xs">
            <Box>
              <Text color="gray.500">Render Time:</Text>
              <Text fontWeight="semibold">{performanceMetrics.renderTime}ms</Text>
            </Box>
            <Box>
              <Text color="gray.500">Data Points:</Text>
              <Text fontWeight="semibold">{performanceMetrics.dataPoints}</Text>
            </Box>
            <Box>
              <Text color="gray.500">Last Update:</Text>
              <Text fontWeight="semibold">
                {performanceMetrics.lastUpdate?.toLocaleTimeString() || 'N/A'}
              </Text>
            </Box>
          </SimpleGrid>
        </Box>

        <Divider mb={2} />

        <VStack align="stretch" spacing={2} fontSize="xs">
          {debugLogs.map((log, index) => (
            <Box
              key={index}
              p={2}
              borderRadius="md"
              bg={
                log.type === 'error'   ? 'red.50'    :
                log.type === 'warn'    ? 'yellow.50' :
                log.type === 'success' ? 'green.50'  : 'white'
              }
              borderWidth="1px"
              borderColor={
                log.type === 'error'   ? 'red.200'    :
                log.type === 'warn'    ? 'yellow.200' :
                log.type === 'success' ? 'green.200'  : 'gray.200'
              }
            >
              <Text
                fontWeight="bold"
                color={
                  log.type === 'error'   ? 'red.700'    :
                  log.type === 'warn'    ? 'yellow.700' :
                  log.type === 'success' ? 'green.700'  : 'gray.700'
                }
              >
                [{new Date(log.timestamp).toLocaleTimeString()}] {log.type.toUpperCase()}: {log.message}
              </Text>
              {log.details && (
                <Code
                  mt={1} p={1} w="100%"
                  fontSize="10px"
                  colorScheme="blackAlpha"
                  whiteSpace="pre-wrap"
                  display="block"
                >
                  {JSON.stringify(log.details, null, 2)}
                </Code>
              )}
            </Box>
          ))}
          {debugLogs.length === 0 && (
            <Text color="gray.500" textAlign="center">No logs yet</Text>
          )}
        </VStack>
      </Box>
    </Collapse>
  );

  // ── Main Content ───────────────────────────────────────────────────────────
  const renderContent = () => {
    if (activeLoading) {
      return (
        <Flex justify="center" align="center" h="300px" direction="column">
          <Spinner size="xl" mb={4} color="blue.400" />
          <Text color="gray.500">Loading asset type analytics...</Text>
          <Progress size="xs" isIndeterminate w="200px" mt={2} colorScheme="blue" />
        </Flex>
      );
    }

    return (
      <Box>
        {renderStats()}

        {/* Selected asset type indicator */}
        {selectedAssetType && (
          <HStack mb={2}>
            <Text fontSize="xs" color="gray.500">Focused:</Text>
            <Tag
              size="sm"
              colorScheme="blue"
              borderRadius="full"
              cursor="pointer"
              onClick={() => setSelectedAssetType(null)}
            >
              <TagLabel>{selectedAssetType} ✕</TagLabel>
            </Tag>
          </HStack>
        )}

        {renderChart()}

        {/* --- UPDATE: FlowSense AI explainer */}
        {normalizedData && (
          <Box mt={4}>
            <FlowSenseAIExplainer
              module="work_orders_by_asset_type"
              title="Explain work by asset type"
              subtitle="Which asset classes generate the most work"
              facts={typeExplainer.facts}
              findings={typeExplainer.findings}
              compact
              questions={['Why does the top asset type need so much work?', 'Where would preventive maintenance cut volume most?']}
            />
          </Box>
        )}
        {/* --- END UPDATE */}
      </Box>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Card h="100%" position="relative">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Flex justify="space-between" align="center" mb={4}>
        <HStack>
          <Heading size="md">Work Orders by Asset Type</Heading>
          {summary && (
            <Badge colorScheme="blue" fontSize="sm">
              {summary.total} Total
            </Badge>
          )}
        </HStack>

        <HStack spacing={2}>
          {/* Refresh */}
          {!externalData && (
            <Tooltip label="Refresh Asset Type Data">
              <IconButton
                icon={<MdRefresh />}
                size="sm"
                variant="outline"
                isLoading={activeLoading}
                onClick={() => fetchAssetData(true)}
              />
            </Tooltip>
          )}

          {/* Chart Type Selector */}
          <Menu>
            <Tooltip label="Chart type">
              <MenuButton
                as={IconButton}
                icon={
                  chartType === 'line'         ? <FaChartLine />          :
                  chartType === 'area'         ? <FaChartArea />          :
                  chartType === 'vertical-bar' ? <FaChartBar />           :
                                                 <BsLayoutSidebarInset />
                }
                size="sm"
                variant="outline"
              />
            </Tooltip>
            <MenuList>
              <MenuItem icon={<BsLayoutSidebarInset />} onClick={() => setChartType('horizontal-bar')}>
                Horizontal Bar (Default)
              </MenuItem>
              <MenuItem icon={<FaChartBar />}           onClick={() => setChartType('vertical-bar')}>
                Vertical Bar
              </MenuItem>
              <MenuItem icon={<FaChartLine />}          onClick={() => setChartType('line')}>
                Line Chart
              </MenuItem>
              <MenuItem icon={<FaChartArea />}          onClick={() => setChartType('area')}>
                Area Chart
              </MenuItem>
            </MenuList>
          </Menu>

          {/* Export */}
          {enableExport && (
            <Tooltip label="Export data">
              <IconButton
                icon={<MdDownload />}
                size="sm"
                variant="outline"
                onClick={handleExport}
                isDisabled={!normalizedData || activeLoading}
              />
            </Tooltip>
          )}

          {/* Settings */}
          <Tooltip label="Chart settings">
            <IconButton
              icon={<MdSettings />}
              size="sm"
              variant="outline"
              onClick={onSettingsOpen}
            />
          </Tooltip>

          {/* Debug Toggle */}
          {enableDebug && (
            <Tooltip label="Toggle debug console">
              <IconButton
                icon={<MdBugReport />}
                size="sm"
                variant="outline"
                colorScheme={showDebug ? 'red' : 'gray'}
                onClick={() => setShowDebug(!showDebug)}
              />
            </Tooltip>
          )}

          {/* Fullscreen */}
          <Tooltip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <IconButton
              icon={isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />}
              size="sm"
              variant="outline"
              onClick={() => setIsFullscreen(!isFullscreen)}
            />
          </Tooltip>
        </HStack>
      </Flex>

      {/* ── Content ────────────────────────────────────────────────────────── */}
      {renderContent()}

      {/* ── Debug Console ──────────────────────────────────────────────────── */}
      {enableDebug && renderDebugConsole()}

      {/* ── Settings Modal ─────────────────────────────────────────────────── */}
      <Modal isOpen={isSettingsOpen} onClose={onSettingsClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Chart Settings</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Chart Type</FormLabel>
                <Select value={chartType} onChange={(e) => setChartType(e.target.value)}>
                  <option value="horizontal-bar">Horizontal Bar (Default)</option>
                  <option value="vertical-bar">Vertical Bar</option>
                  <option value="line">Line Chart</option>
                  <option value="area">Area Chart</option>
                </Select>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" mb={0}>Show Data Labels</FormLabel>
                <Switch
                  isChecked={showDataLabels}
                  onChange={(e) => setShowDataLabels(e.target.checked)}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" mb={0}>Per-Asset Color Coding</FormLabel>
                <Switch
                  isChecked={useAssetColors}
                  onChange={(e) => setUseAssetColors(e.target.checked)}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" mb={0}>Show Grid Lines</FormLabel>
                <Switch
                  isChecked={showGrid}
                  onChange={(e) => setShowGrid(e.target.checked)}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" mb={0}>Show Legend</FormLabel>
                <Switch
                  isChecked={showLegend}
                  onChange={(e) => setShowLegend(e.target.checked)}
                />
              </FormControl>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default WorkOrderAssetTypeChart;